import { ALBUM_MAX_MIDIAS, aceitaAlbum } from '@empire/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useRef, useState } from 'react'
import { BotaoGravarAudio } from '../../components/BotaoGravarAudio'
import { InserirVariavel } from '../../components/InserirVariavel'
import { IconDoc, IconPaperclip, IconVideoCircular } from '../../components/icons'
import { Modal } from '../../components/Modal'
import { t, tRico } from '../../lib/i18n'
import { useWorkspaceRole } from '../../lib/role'
import { useTRPC } from '../../lib/trpc'
import { conferirVideoCircular } from '../../lib/video-circular'

export const Route = createFileRoute('/_app/respostas')({ component: QuickRepliesPage })

/**
 * Respostas rápidas.
 *
 * Tela própria porque é conteúdo de trabalho — texto que a equipe manda o dia
 * inteiro —, e não configuração do sistema. Dentro de Configurações ela ficava
 * embaixo de inbox e convite, coisas que se mexe uma vez por mês.
 */
function QuickRepliesPage() {
  const [criando, setCriando] = useState(false)

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-3xl px-5 pt-4 md:pt-8">
        {/* Título e botão na mesma linha, descrição embaixo — o mesmo desenho
            de Campanhas e Transmissão. */}
        <div className="flex items-center gap-3">
          <h1 className="min-w-0 font-display text-2xl">{t('Respostas rápidas')}</h1>
          <button
            type="button"
            onClick={() => setCriando(true)}
            className="ml-auto shrink-0 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:bg-accent-ink"
          >
            {t('Nova resposta')}
          </button>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-ink-2">
          {tRico(
            'Digite {barra} no campo de mensagem para abrir a lista, ou use o botão ao lado do anexo. O atalho não pode ter espaços.',
            { barra: <code className="rounded bg-surface-2 px-1">/</code> },
          )}
        </p>

        <div className="mt-6">
          <QuickRepliesSection criando={criando} onFecharCriacao={() => setCriando(false)} />
        </div>
      </div>
    </div>
  )
}

function QuickRepliesSection({
  criando,
  onFecharCriacao,
}: {
  criando: boolean
  onFecharCriacao: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const replies = useQuery(trpc.quickReply.list.queryOptions({}))
  const inboxes = useQuery(trpc.inbox.list.queryOptions())
  const { isAdmin } = useWorkspaceRole()

  const [shortcut, setShortcut] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  // `null` = vale para todos os inboxes. Só admin cria assim, então para
  // atendente o padrão é o primeiro inbox que ele enxerga.
  const [alvo, setAlvo] = useState<string | null>(null)

  const primeiroInbox = inboxes.data?.[0]?.id ?? null
  const inboxAlvo = isAdmin ? alvo : (alvo ?? primeiroInbox)

  /**
   * Anexo fixo da resposta. Sobe uma vez e é reutilizado a cada envio — é o
   * caso de tabela de preços, contrato ou catálogo, que não muda por conversa.
   */
  const corpoRef = useRef<HTMLTextAreaElement>(null)
  const anexoRef = useRef<HTMLInputElement>(null)
  const circularRef = useRef<HTMLInputElement>(null)
  /** Aviso do vídeo circular — corte por não ser quadrado, por exemplo. */
  const [avisoCircular, setAvisoCircular] = useState<string | null>(null)
  /**
   * Os anexos da resposta, na ordem em que foram escolhidos.
   *
   * Dois ou mais viram um álbum no Telegram — chegam juntos, numa grade. Nem
   * todo anexo pode entrar num: áudio de voz e vídeo circular são mensagens
   * inteiras por si, então quando um deles está na lista ela para de crescer.
   */
  const [anexos, setAnexos] = useState<Array<{ mediaId: string; kind: string; fileName: string }>>(
    [],
  )
  const [subindo, setSubindo] = useState(false)
  const [erroAnexo, setErroAnexo] = useState<string | null>(null)

  /**
   * Cabe mais um anexo?
   *
   * Só até o limite do álbum, e só enquanto tudo que está na lista puder
   * conviver dentro de um — um vídeo circular sozinho já fecha a lista.
   */
  const podeAnexarMais =
    anexos.length < ALBUM_MAX_MIDIAS && anexos.every((item) => aceitaAlbum(item.kind))

  /** `extras` carrega duração e intenção quando o arquivo veio do gravador. */
  async function subirAnexo(file: File, extras?: { duracao: number; kind: string }) {
    setSubindo(true)
    setErroAnexo(null)
    try {
      const form = new FormData()
      form.set('file', file)
      if (extras) {
        form.set('duration', String(extras.duracao))
        form.set('kind', extras.kind)
      }
      // Sem `conversationId`: o anexo é do workspace, não de um atendimento.
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: form,
        credentials: 'include',
        headers: { 'x-workspace-id': localStorage.getItem('empire:workspace') ?? '' },
      })
      const result = (await response.json()) as {
        mediaId?: string
        kind?: string
        fileName?: string
        error?: string
      }

      if (!response.ok || !result.mediaId) {
        setErroAnexo(result.error ?? t('Falha ao enviar o arquivo'))
        return
      }

      setAnexos((atuais) => [
        ...atuais,
        {
          mediaId: result.mediaId as string,
          kind: result.kind ?? 'document',
          fileName: result.fileName ?? file.name,
        },
      ])
    } catch {
      setErroAnexo(t('Falha de rede ao enviar o arquivo'))
    } finally {
      setSubindo(false)
    }
  }

  /**
   * Confere o vídeo antes de subir. A recusa do Telegram só apareceria depois,
   * no primeiro uso da resposta, com o cliente do outro lado esperando.
   */
  async function anexarCircular(file: File) {
    setErroAnexo(null)
    setAvisoCircular(null)

    const veredito = await conferirVideoCircular(file)
    if ('erro' in veredito) {
      setErroAnexo(veredito.erro)
      return
    }

    setAvisoCircular(veredito.aviso)
    await subirAnexo(file, { duracao: 0, kind: 'video_note' })
  }

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: [['quickReply']] })
  }

  const create = useMutation({
    ...trpc.quickReply.create.mutationOptions(),
    onSuccess: () => {
      setShortcut('')
      setTitle('')
      setBody('')
      setAnexos([])
      onFecharCriacao()
      invalidate()
    },
  })
  const remove = useMutation({
    ...trpc.quickReply.remove.mutationOptions(),
    onSuccess: invalidate,
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    create.mutate({
      shortcut,
      title,
      body,
      inboxId: inboxAlvo,
      anexos: anexos.map((item) => ({
        mediaId: item.mediaId,
        kind: item.kind as 'photo' | 'document' | 'video_note',
      })),
    })
  }

  return (
    <div>
      {criando && (
        <Modal
          title={t('Nova resposta rápida')}
          description={t('O atalho é o que se digita depois da barra no campo de mensagem.')}
          onClose={onFecharCriacao}
        >
          <form onSubmit={submit} className="flex flex-col gap-3 p-4">
            <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
              <Field label={t('Atalho')}>
                <span className="flex items-center gap-1">
                  <span className="text-sm font-semibold text-ink-3">/</span>
                  <input
                    value={shortcut}
                    onChange={(event) => setShortcut(event.target.value)}
                    required
                    pattern="[A-Za-z0-9_-]{1,24}"
                    placeholder={t('proposta')}
                    className="field"
                  />
                </span>
              </Field>
              <Field label={t('Título')}>
                <input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  placeholder={t('Enviar proposta padrão')}
                  className="field"
                />
              </Field>
            </div>

            <Field label={t('Texto')}>
              <textarea
                ref={corpoRef}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                required
                rows={3}
                placeholder={t(
                  'Segue nossa proposta padrão. Qualquer dúvida sobre os planos, me chama.',
                )}
                className="field resize-y"
              />
              {/* As variáveis são resolvidas quando a resposta entra no campo
                  de mensagem, então quem atende lê o texto final antes de
                  mandar. */}
              <InserirVariavel campo={corpoRef} valor={body} onInserir={setBody} />
            </Field>

            <Field label={t('Onde vale')}>
              <select
                value={inboxAlvo ?? 'todos'}
                onChange={(event) =>
                  setAlvo(event.target.value === 'todos' ? null : event.target.value)
                }
                className="field"
              >
                <option value="todos" disabled={!isAdmin}>
                  {t('Todos os inboxes')}
                </option>
                {(inboxes.data ?? []).map((row) => (
                  <option key={row.id} value={row.id}>
                    {t('Só em {nome}', { nome: row.name })}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={anexos.length > 1 ? t('Anexos (opcional)') : t('Anexo (opcional)')}>
              <input
                ref={anexoRef}
                type="file"
                className="hidden"
                multiple
                onChange={(event) => {
                  const escolhidos = Array.from(event.target.files ?? [])
                  event.target.value = ''
                  // Um de cada vez, em sequência: `subirAnexo` acrescenta à
                  // lista, e subir em paralelo embaralharia a ordem do álbum.
                  void (async () => {
                    for (const file of escolhidos.slice(0, ALBUM_MAX_MIDIAS - anexos.length)) {
                      await subirAnexo(file)
                    }
                  })()
                }}
              />
              {/* Entrada separada, e só MP4: vídeo circular é uma escolha de
              como a mensagem chega, não um mime que o servidor adivinhe. */}
              <input
                ref={circularRef}
                type="file"
                accept="video/mp4"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ''
                  if (file) void anexarCircular(file)
                }}
              />

              {anexos.length > 0 && (
                <span className="flex flex-col gap-1.5">
                  {anexos.map((item, indice) => (
                    <span
                      key={item.mediaId}
                      className="flex items-center gap-2 rounded-md border border-line bg-surface-2 px-2.5 py-1.5"
                    >
                      {item.kind === 'video_note' ? (
                        <IconVideoCircular className="size-4 flex-none text-ink-3" />
                      ) : (
                        <IconDoc className="size-4 flex-none text-ink-3" />
                      )}
                      <span className="min-w-0 flex-1 truncate text-xs">
                        {/* A numeração só aparece no álbum, que é onde a ordem
                            muda o que o cliente vê. */}
                        {anexos.length > 1 && (
                          <span className="mr-1 text-ink-3">{indice + 1}.</span>
                        )}
                        {item.fileName}
                        {item.kind === 'video_note' && (
                          <span className="ml-1 text-ink-3">· {t('vídeo circular')}</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setAnexos((atuais) => atuais.filter((outro) => outro !== item))
                        }
                        aria-label={t('Remover anexo')}
                        className="flex-none text-2xs text-ink-3 hover:text-danger"
                      >
                        {t('Remover')}
                      </button>
                    </span>
                  ))}
                </span>
              )}

              {podeAnexarMais && (
                <span className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => anexoRef.current?.click()}
                    disabled={subindo}
                    className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-line-strong px-2.5 py-1.5 text-xs font-semibold text-ink-3 hover:border-ink-3 hover:text-ink disabled:opacity-50"
                  >
                    <IconPaperclip className="size-4" />
                    {subindo
                      ? t('Enviando…')
                      : anexos.length > 0
                        ? t('Anexar mais')
                        : t('Anexar imagem ou arquivo')}
                  </button>

                  {/* Áudio e vídeo circular são mensagens inteiras por si, e o
                  Telegram não os aceita dentro de um álbum: os dois só
                  aparecem enquanto não há nenhum anexo escolhido. */}
                  {anexos.length === 0 && (
                    <>
                      <BotaoGravarAudio
                        disabled={subindo}
                        onGravado={(audio) =>
                          void subirAnexo(
                            new File([audio.blob], audio.fileName, { type: audio.blob.type }),
                            { duracao: audio.duracao, kind: audio.kind },
                          )
                        }
                      />

                      <button
                        type="button"
                        onClick={() => circularRef.current?.click()}
                        disabled={subindo}
                        title={t('Vídeo circular (nota de vídeo)')}
                        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-line-strong px-2.5 py-1.5 text-xs font-semibold text-ink-3 hover:border-ink-3 hover:text-ink disabled:opacity-50"
                      >
                        <IconVideoCircular className="size-4" />
                        {t('Vídeo circular')}
                      </button>
                    </>
                  )}
                </span>
              )}

              <span className="mt-1 block text-2xs text-ink-3">
                {/* O arquivo sobe uma vez só: usar a resposta dez vezes não gera
                dez cópias no armazenamento. */}
                {t('O arquivo sobe uma vez e vai junto toda vez que a resposta for usada.')}
              </span>

              {anexos.length > 1 && (
                <span className="mt-1 block text-2xs text-ink-3">
                  {t(
                    'Com dois ou mais anexos a resposta chega como um álbum: as mídias aparecem juntas, numa grade, e o texto vira a legenda da primeira.',
                  )}
                </span>
              )}

              {anexos.length >= ALBUM_MAX_MIDIAS && (
                <span className="mt-1 block text-2xs text-ink-3">
                  {t('Um álbum leva no máximo {n} mídias.', { n: ALBUM_MAX_MIDIAS })}
                </span>
              )}

              {anexos.some((item) => item.kind === 'video_note') && (
                <span className="mt-1 block text-2xs text-ink-3">
                  {t(
                    'Vídeo circular não tem legenda no Telegram. O vídeo chega primeiro e o texto da resposta vem logo depois, numa mensagem própria.',
                  )}
                </span>
              )}

              {avisoCircular && (
                <span
                  className="mt-1 block text-2xs font-semibold"
                  style={{ color: 'var(--tag-amber)' }}
                >
                  {avisoCircular}
                </span>
              )}

              {erroAnexo && (
                <span role="alert" className="mt-1 block text-2xs text-danger">
                  {erroAnexo}
                </span>
              )}
            </Field>

            {create.error && (
              <p role="alert" className="text-xs text-danger">
                {t(create.error.message)}
              </p>
            )}

            <button
              type="submit"
              disabled={create.isPending}
              className="self-start rounded-md bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-50"
            >
              {create.isPending ? t('Salvando…') : t('Adicionar')}
            </button>
          </form>
        </Modal>
      )}

      {!replies.isPending && (replies.data ?? []).length === 0 && (
        <p className="rounded-lg border border-dashed border-line-strong px-4 py-8 text-center text-xs text-ink-3">
          {t('Nenhuma resposta rápida ainda. Crie a primeira em “Nova resposta”.')}
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {(replies.data ?? []).map((reply) => (
          <li
            key={reply.id}
            className="flex items-start gap-3 rounded-lg border border-line bg-surface px-3 py-2.5"
          >
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="font-semibold text-accent-ink">/{reply.shortcut}</span>
                <span className="text-sm font-semibold">{reply.title}</span>
                <span className="rounded-full bg-surface-2 px-1.5 py-px text-[10px] font-semibold text-ink-3">
                  {reply.inboxId
                    ? (inboxes.data?.find((row) => row.id === reply.inboxId)?.name ?? t('Um inbox'))
                    : t('Todos')}
                </span>
              </span>
              <span className="mt-0.5 block truncate text-2xs text-ink-3">{reply.body}</span>
              {reply.anexos.length > 0 && (
                <span className="mt-1 inline-flex items-center gap-1 text-2xs text-ink-3">
                  <IconPaperclip className="size-3" />
                  {reply.anexos.length > 1
                    ? t('álbum de {n} mídias', { n: reply.anexos.length })
                    : (reply.anexos[0]?.fileName ?? t('anexo'))}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => remove.mutate({ id: reply.id })}
              className="flex-none text-2xs text-ink-3 hover:text-danger"
            >
              {t('Remover')}
            </button>
          </li>
        ))}
      </ul>

      <style>{`
        .field {
          width: 100%;
          border-radius: 6px;
          border: 1px solid var(--line-strong);
          background: var(--surface);
          padding: 7px 10px;
          font-size: 13px;
          color: var(--ink);
        }
      `}</style>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-2xs font-bold uppercase tracking-wider text-ink-3">
        {label}
      </span>
      {children}
    </label>
  )
}
